import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import interfaceImage from '../components/interface_images/interface_1.png';
import ScrollProgressBar from '../components/ScrollProgressBar';
import './ProductPage.css';

const PRODUCT_METADATA = {
  title: 'Rusiv — Personal AI Companion | Allyverto',
  description:
    'Meet Rusiv, a personal AI companion designed to understand your context, remember what matters with your control, and help across conversations, decisions, learning, research, documents, and everyday life.',
  canonical: 'https://allyverto.com/rusiv',
  image: 'https://allyverto.com/logo.png',
};

const usePageMetadata = ({
  title,
  description,
  canonical,
  image,
}) => {
  useEffect(() => {
    const previousTitle = document.title;
    const updatedElements = [];

    const updateMeta = (selector, attributes, content) => {
      let element = document.head.querySelector(selector);
      const created = !element;

      if (!element) {
        element = document.createElement('meta');
        Object.entries(attributes).forEach(([name, value]) => {
          element.setAttribute(name, value);
        });
        document.head.appendChild(element);
      }

      updatedElements.push({
        element,
        created,
        previousContent: element.getAttribute('content'),
      });
      element.setAttribute('content', content);
    };

    document.title = title;
    updateMeta('meta[name="title"]', { name: 'title' }, title);
    updateMeta('meta[name="description"]', { name: 'description' }, description);
    updateMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    updateMeta(
      'meta[property="og:description"]',
      { property: 'og:description' },
      description
    );
    updateMeta('meta[property="og:url"]', { property: 'og:url' }, canonical);
    updateMeta('meta[property="og:image"]', { property: 'og:image' }, image);
    updateMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    updateMeta(
      'meta[name="twitter:description"]',
      { name: 'twitter:description' },
      description
    );
    updateMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image);

    let canonicalElement = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonicalElement;
    const previousCanonical = canonicalElement?.getAttribute('href');

    if (!canonicalElement) {
      canonicalElement = document.createElement('link');
      canonicalElement.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.setAttribute('href', canonical);

    return () => {
      document.title = previousTitle;
      updatedElements.forEach(({ element, created, previousContent }) => {
        if (created) {
          element.remove();
        } else if (previousContent === null) {
          element.removeAttribute('content');
        } else {
          element.setAttribute('content', previousContent);
        }
      });

      if (canonicalCreated) {
        canonicalElement.remove();
      } else if (previousCanonical === null) {
        canonicalElement.removeAttribute('href');
      } else {
        canonicalElement.setAttribute('href', previousCanonical);
      }
    };
  }, [canonical, description, image, title]);
};

const useReducedMotion = () => {
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event) => setReducedMotion(event.matches);

    setReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return reducedMotion;
};

const useProductScrollReveal = (scopeRef) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return undefined;

    const targets = scope.querySelectorAll('[data-rusiv-reveal]');
    if (!('IntersectionObserver' in window)) {
      targets.forEach((element) => element.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '-32px' }
    );

    targets.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [scopeRef]);
};

const differenceColumns = [
  {
    id: 'generic',
    eyebrow: 'A typical chatbot',
    title: 'It mainly sees the message in front of it.',
    points: [
      'Often starts fresh each time',
      'Answers the question you just asked',
      'May give the same answer to many people',
      'Knows little about the person asking',
      'Can lose the story between chats',
    ],
  },
  {
    id: 'rusiv',
    eyebrow: 'Rusiv',
    title: 'It can understand the person asking.',
    points: [
      'Uses what it knows when it can help',
      'Learns how you like to talk',
      'Remembers important things with your control',
      'Connects today with earlier chats',
      'Gets more helpful as it knows you better',
    ],
  },
];

const personalContext = [
  'How you like to talk',
  'Your goals',
  'People who matter',
  'Choices you made',
  'Daily routines',
  'Things you enjoy',
  'How you learn',
  'Work in progress',
  'How you are feeling',
  'Things you ask it to remember',
];

const capabilities = [
  {
    id: 'conversations',
    symbol: '↔',
    title: 'Natural Conversations',
    description:
      'Talk in your own way. Rusiv follows the conversation and gives an answer that fits you.',
    detail:
      'Come back to an idea, finish an old thought, or talk something through without writing a perfect prompt.',
  },
  {
    id: 'memory',
    symbol: '◌',
    title: 'Helpful Memory',
    description:
      'Rusiv can remember useful things you share, so you do not have to explain everything again.',
    detail:
      'This may include your likes, goals, routines, important people, and things you ask Rusiv to remember.',
  },
  {
    id: 'research',
    symbol: '⌁',
    title: 'Web Research',
    description:
      'Rusiv can search the web, compare information, and explain what it finds in clear words.',
    detail:
      'It brings the useful parts back to your chat and tells you when something is not clear.',
  },
  {
    id: 'documents',
    symbol: '▤',
    title: 'Understand Documents',
    description:
      'Share a document and ask Rusiv to explain, shorten, compare, or help you use it.',
    detail:
      'Ask about details, connect ideas, and turn a long document into clear next steps.',
  },
  {
    id: 'voice',
    symbol: '◉',
    title: 'Talk With Your Voice',
    description:
      'Speak when you do not want to type and keep the same conversation going.',
    detail:
      'Think out loud, ask a quick question, or keep talking while you are on the move.',
  },
  {
    id: 'decisions',
    symbol: '◇',
    title: 'Help With Choices',
    description:
      'Compare choices, see what may go wrong, and find a good next step.',
    detail:
      'Rusiv helps you see the choice clearly. You still make the final decision.',
  },
  {
    id: 'emotion',
    symbol: '♡',
    title: 'Understands Feelings',
    description:
      'Rusiv pays attention to how you feel and answers with care, not like a cold machine.',
    detail:
      'It can gently share what it notices and ask what kind of help would feel right.',
  },
  {
    id: 'everyday',
    symbol: '✦',
    title: 'Everyday Help',
    description:
      'Plan your day, learn something, organise work, or continue a task you started before.',
    detail:
      'Use Rusiv for work, learning, planning, and the small things that keep life moving.',
  },
];

const chatChapters = [
  {
    id: 'remembering',
    label: 'Rusiv remembers',
    mood: 'continuity',
    atmosphere: ['tomorrow', 'opening', 'team', 'keep it simple'],
    messages: [
      {
        role: 'user',
        text: 'I have that presentation tomorrow. I still feel like I’m not ready.',
      },
      {
        role: 'rusiv',
        text: 'The product review with your team, right? Last time you said the opening was the part making you most nervous. We can work on only that first.',
      },
      {
        role: 'user',
        text: 'Yes. I keep trying to explain everything at once.',
      },
      {
        role: 'rusiv',
        text: 'Then let’s make it simpler. Start with the problem, show what changed, and finish with the decision you need from them. Send me your current opening and we’ll shorten it together.',
      },
    ],
  },
  {
    id: 'understanding',
    label: 'Rusiv notices how you feel',
    mood: 'care',
    atmosphere: ['what is finished', 'your pace', 'a clear first step', 'your choice'],
    messages: [
      {
        role: 'user',
        text: 'I don’t think I did enough today.',
      },
      {
        role: 'rusiv',
        text: 'It sounds like you’re judging the day only by what is unfinished. You completed the client revision and fixed the login issue you were blocked on yesterday.',
      },
      {
        role: 'user',
        text: 'But the main task is still pending.',
      },
      {
        role: 'rusiv',
        text: 'That matters, but it does not erase what you finished. Do you want to make a small plan for tonight, or stop here and continue tomorrow with a clear first step?',
      },
    ],
  },
  {
    id: 'deciding',
    label: 'Rusiv helps you choose',
    mood: 'clarity',
    atmosphere: ['stability', 'onboarding', 'user need', 'three options'],
    messages: [
      {
        role: 'user',
        text: 'I’m thinking about adding another major feature before launch.',
      },
      {
        role: 'rusiv',
        text: 'You previously wanted this release to stay focused on stability and onboarding. What problem would the new feature solve before launch?',
      },
      {
        role: 'user',
        text: 'Honestly, it is mostly because a competitor released something similar.',
      },
      {
        role: 'rusiv',
        text: 'Then the decision may be about pressure, not user need. We can compare three options: ship it now, test a smaller version, or keep the launch focused and review it after real user feedback.',
      },
    ],
  },
  {
    id: 'knowledge',
    label: 'Rusiv finds useful information',
    mood: 'knowledge',
    atmosphere: ['plan', 'notes', 'payment steps', 'missing parts'],
    messages: [
      {
        role: 'user',
        text: 'Can you compare this plan with the notes I shared yesterday?',
      },
      {
        role: 'rusiv',
        text: 'Yes. I’ll check what matches, what is missing, and which dates are different.',
      },
      {
        role: 'user',
        text: 'Please check the payment steps carefully.',
      },
      {
        role: 'rusiv',
        text: 'The plan explains how people pay, but not what happens when a payment fails or needs a refund. I’ll list those missing parts first.',
      },
    ],
  },
];

const helpSteps = [
  {
    number: '01',
    title: 'You speak naturally',
    description:
      'Ask a question or explain what is on your mind. You do not need a perfect prompt.',
  },
  {
    number: '02',
    title: 'Rusiv understands what you need',
    description:
      'It looks past the exact words to the actual need behind them.',
  },
  {
    number: '03',
    title: 'It remembers helpful details',
    description:
      'When it helps, Rusiv can connect your question with things you shared before.',
  },
  {
    number: '04',
    title: 'It finds the right information',
    description:
      'It may use the web, a document, a tool, or details from your chat.',
  },
  {
    number: '05',
    title: 'It puts the answer in order',
    description:
      'The important parts come first, and anything unclear gets flagged instead of hidden.',
  },
  {
    number: '06',
    title: 'You decide next',
    description:
      'Rusiv shapes the answer around your situation. The final call is always yours.',
  },
];

const technicalDetails = [
  {
    title: 'Reasoning',
    text: 'Rusiv can break a hard question into smaller, clearer parts.',
  },
  {
    title: 'Search',
    text: 'Rusiv can search the web when you need new information.',
  },
  {
    title: 'Helpful memory',
    text: 'It can use a helpful memory when it fits your question.',
  },
  {
    title: 'Documents and tools',
    text: 'It can work with your documents and useful AI tools.',
  },
  {
    title: 'Checking',
    text: 'It can check sources and say when something is not certain.',
  },
];

const controlPrinciples = [
  {
    title: 'Understand what is remembered',
    text: 'See exactly what Rusiv has learned about you, in plain language.',
  },
  {
    title: 'Fix old information',
    text: 'Life changes quickly. Correct anything that no longer feels true.',
  },
  {
    title: 'Delete what you do not want',
    text: 'Remove any memory you would rather Rusiv forget.',
  },
  {
    title: 'Handle personal details with care',
    text: 'Sensitive details are treated carefully, and only ever used to help you.',
  },
];

const RevealHeader = ({
  eyebrow,
  title,
  description,
  id,
  variant = 'split',
}) => (
  <header
    className={`rp-section-header rp-section-header-${variant}`}
    data-rusiv-reveal
  >
    <p className="rp-eyebrow">{eyebrow}</p>
    <h2 id={id}>{title}</h2>
    {description && <p className="rp-section-intro">{description}</p>}
  </header>
);

const RusivHero = () => (
  <section className="rp-hero" aria-labelledby="rp-hero-title">
    <div className="rp-hero-light rp-hero-light-one" aria-hidden="true" />
    <div className="rp-hero-light rp-hero-light-two" aria-hidden="true" />
    <div className="rp-container">
      <div className="rp-hero-copy">
        <p className="rp-eyebrow rp-hero-eyebrow">Meet Rusiv</p>
        <h1 id="rp-hero-title">
          Built to know you
          <br/>Made to grow with you.
        </h1>
        <p className="rp-hero-statement">
          Rusiv is a personal AI companion that learns what matters to you and
          helps with the small and big parts of every day.
        </p>
        <p className="rp-brand-line" aria-label="you with you">
          you with <strong>YOU!</strong>
        </p>
        <div className="rp-actions rp-hero-actions">
          <a
            className="rp-button rp-button-primary"
            href="https://rusiv.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Try Rusiv
          </a>
          <a className="rp-button rp-button-secondary" href="#understands-you">
            See how Rusiv helps
          </a>
        </div>
      </div>
    </div>
  </section>
);

const RusivDifference = () => {
  const [activeColumn, setActiveColumn] = useState('rusiv');

  return (
    <section
      id="difference"
      className="rp-section rp-difference"
      aria-labelledby="rp-difference-title"
    >
      <div className="rp-container">
        <RevealHeader
          eyebrow="Where Rusiv is different"
          id="rp-difference-title"
          title={
            <>
              A chatbot sees a question. <em>Rusiv sees you.</em>
            </>
          }
          description="Most chatbots answer one message. Rusiv can use what it knows about you to give help that fits."
        />

        <div
          className="rp-comparison-controls"
          aria-label="Choose a side to compare"
          data-rusiv-reveal
        >
          {differenceColumns.map((column) => (
            <button
              type="button"
              className={activeColumn === column.id ? 'is-active' : ''}
              aria-pressed={activeColumn === column.id}
              onClick={() => setActiveColumn(column.id)}
              key={column.id}
            >
              {column.eyebrow}
            </button>
          ))}
        </div>

        <div className="rp-comparison-grid">
          {differenceColumns.map((column) => (
            <article
              className={`rp-comparison-card rp-comparison-card-${column.id}${
                activeColumn === column.id ? ' is-active' : ''
              }`}
              data-rusiv-reveal
              key={column.id}
            >
              <p className="rp-card-eyebrow">{column.eyebrow}</p>
              <h3>{column.title}</h3>
              <ul>
                {column.points.map((point) => (
                  <li key={point}>
                    <span aria-hidden="true">
                      {column.id === 'rusiv' ? '✓' : '—'}
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <p className="rp-comparison-note" data-rusiv-reveal>
          Rusiv answers you, not just your latest message.
        </p>
      </div>
    </section>
  );
};

const PersonalUnderstanding = () => (
  <section
    id="understands-you"
    className="rp-section rp-understanding"
    aria-labelledby="rp-understanding-title"
  >
    <div className="rp-container rp-understanding-grid">
      <div className="rp-understanding-copy" data-rusiv-reveal>
        <p className="rp-eyebrow">It learns with you</p>
        <h2 id="rp-understanding-title">
          <em>Remembered,</em> not repeated.
        </h2>
        <p>
          Over time, Rusiv can learn how you like to talk, what you care about,
          and what you are working on. You choose what to share.
        </p>
        <div className="rp-control-promise">
          <span aria-hidden="true">✓</span>
          <strong>You choose what Rusiv remembers.</strong>
        </div>
      </div>

      <div
        className="rp-understanding-visual"
        aria-label="Examples of things Rusiv can remember when they are helpful"
        data-rusiv-reveal
      >
        <div className="rp-understanding-core">
          <span>You</span>
          <small>at the centre</small>
        </div>
        <ul>
          {personalContext.map((item, index) => (
            <li className={`rp-memory-item rp-memory-item-${index + 1}`} key={item}>
              <span aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
        <p className="rp-visual-caption">
          It learns slowly, one useful detail at a time.
        </p>
      </div>
    </div>
  </section>
);

const CapabilityCard = ({ capability, index }) => {
  const [expanded, setExpanded] = useState(false);
  const detailId = `rp-capability-detail-${capability.id}`;

  return (
    <article
      className={`rp-capability-card rp-capability-card-${(index % 4) + 1}`}
      data-rusiv-reveal
    >
      <span className="rp-capability-symbol" aria-hidden="true">
        {capability.symbol}
      </span>
      <h3>{capability.title}</h3>
      <p>{capability.description}</p>
      <button
        type="button"
        className="rp-capability-toggle"
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? 'Show less' : 'See how it helps'}
        <span aria-hidden="true">{expanded ? '−' : '+'}</span>
      </button>
      <div
        id={detailId}
        className="rp-capability-detail"
        hidden={!expanded}
      >
        <p>{capability.detail}</p>
      </div>
    </article>
  );
};

const RusivCapabilities = () => (
  <section
    className="rp-section rp-capabilities"
    aria-labelledby="rp-capabilities-title"
  >
    <div className="rp-container">
      <RevealHeader
        eyebrow="Many ways to help"
        id="rp-capabilities-title"
        title={
          <>
            Help for <em>work, learning,</em> and <em>daily life.</em>
          </>
        }
        description="Chat, search the web, understand a document, make a plan, or think through a choice all with the same companion."
        variant="center"
      />
      <div className="rp-capability-grid">
        {capabilities.map((capability, index) => (
          <CapabilityCard
            capability={capability}
            index={index}
            key={capability.id}
          />
        ))}
      </div>
    </div>
  </section>
);

const INTERFACE_HOTSPOTS = [
  {
    id: 'ask',
    label: 'Ask anything field',
    detail: 'Type a question in your own words, no special prompt format needed.',
    top: 49.7,
    left: 52.7,
  },
  {
    id: 'search',
    label: 'Search control',
    detail: 'Turn on live web search when an answer needs current information.',
    top: 56.2,
    left: 33.4,
  },
  {
    id: 'voice',
    label: 'Voice control',
    detail: 'Speak instead of typing and keep the same conversation going.',
    top: 56.1,
    left: 72.75,
  },
  {
    id: 'private',
    label: 'Private mode',
    detail: 'Switch a conversation to private when you would rather it not be remembered.',
    top: 3.5,
    left: 95.6,
  },
  {
    id: 'prompts',
    label: 'Suggested prompts',
    detail: 'Quick-start actions like breaking a task into steps or summarising a document.',
    top: 67.7,
    left: 55,
  },
];

const RusivInterface = ({ reducedMotion }) => {
  const sectionRef = useRef(null);
  const frameRef = useRef(null);
  const [progress, setProgress] = useState(reducedMotion ? 1 : 0);
  const [activeHotspot, setActiveHotspot] = useState(null);

  useEffect(() => {
    if (reducedMotion) {
      setProgress(1);
      return undefined;
    }

    const updateProgress = () => {
      frameRef.current = null;
      if (!sectionRef.current) return;

      const { top } = sectionRef.current.getBoundingClientRect();
      const nextProgress = Math.max(
        0,
        Math.min(1, (window.innerHeight - top) / (window.innerHeight * 0.72))
      );
      setProgress((current) =>
        Math.abs(current - nextProgress) > 0.01 ? nextProgress : current
      );
    };

    const requestUpdate = () => {
      if (frameRef.current === null) {
        frameRef.current = window.requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [reducedMotion]);

  return (
    <section
      ref={sectionRef}
      className="rp-section rp-interface"
      aria-labelledby="rp-interface-title"
    >
      <div className="rp-container">
        <RevealHeader
          eyebrow="Everything in one place"
          id="rp-interface-title"
          title={
            <>
              <em>Talk, search, speak,</em> and <em>come back anytime.</em>
            </>
          }
          description="Your chats, tools, and helpful memories stay together, so it is easy to pick up where you left off."
        />
        <ul className="rp-interface-labels" data-rusiv-reveal>
          <li>Conversations</li>
          <li>Search</li>
          <li>Voice</li>
          <li>Private workspace</li>
        </ul>
      </div>

      <div className="rp-interface-frame-outer">
        <figure
          className="rp-interface-frame"
          style={{ '--rp-interface-progress': progress }}
        >
          <img
            src={interfaceImage}
            width="2940"
            height="1602"
            loading="lazy"
            decoding="async"
            alt="Rusiv’s dark conversation workspace showing an Ask anything field, Search control, voice control, private mode, and suggested prompts."
          />

          <div className="rp-interface-hotspots" aria-hidden={progress < 0.6}>
            {INTERFACE_HOTSPOTS.map((hotspot) => (
              <button
                type="button"
                key={hotspot.id}
                className={`rp-hotspot${activeHotspot === hotspot.id ? ' is-active' : ''}`}
                style={{ top: `${hotspot.top}%`, left: `${hotspot.left}%` }}
                onMouseEnter={() => setActiveHotspot(hotspot.id)}
                onMouseLeave={() =>
                  setActiveHotspot((current) => (current === hotspot.id ? null : current))
                }
                onFocus={() => setActiveHotspot(hotspot.id)}
                onBlur={() =>
                  setActiveHotspot((current) => (current === hotspot.id ? null : current))
                }
                onClick={() =>
                  setActiveHotspot((current) => (current === hotspot.id ? null : hotspot.id))
                }
                tabIndex={progress < 0.6 ? -1 : 0}
                aria-expanded={activeHotspot === hotspot.id}
              >
                <span className="rp-hotspot-dot" aria-hidden="true" />
                <span className="rp-hotspot-tooltip">
                  <strong>{hotspot.label}</strong>
                  <span>{hotspot.detail}</span>
                </span>
                <span className="rp-sr-only">{hotspot.label}</span>
              </button>
            ))}
          </div>

          <figcaption>
            Current Rusiv interface preview. Product details may continue to evolve.
          </figcaption>
        </figure>
      </div>
    </section>
  );
};

const HowRusivHelps = () => (
  <section
    className="rp-section rp-how"
    aria-labelledby="rp-how-title"
  >
    <div className="rp-container">
      <div className="rp-how-layout">
        <RevealHeader
          eyebrow="How it works"
          id="rp-how-title"
          title={
            <>
              You talk. <em>Rusiv understands.</em> Then it helps.
            </>
          }
          description="You do not need special words or a perfect prompt. Just say what you need in your own way."
          variant="side"
        />

        <ol className="rp-help-steps">
          {helpSteps.map((step) => (
            <li data-rusiv-reveal key={step.number}>
              <span className="rp-step-number">{step.number}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <details className="rp-technical-details" data-rusiv-reveal>
        <summary>
          What may support a response
          <span aria-hidden="true">+</span>
        </summary>
        <div className="rp-technical-grid">
          {technicalDetails.map((detail) => (
            <article key={detail.title}>
              <h3>{detail.title}</h3>
              <p>{detail.text}</p>
            </article>
          ))}
        </div>
      </details>
    </div>
  </section>
);

const PrivacyAndControl = () => (
  <section
    id="privacy"
    className="rp-section rp-privacy"
    aria-labelledby="rp-privacy-title"
  >
    <div className="rp-container">
      <div className="rp-privacy-layout">
        <RevealHeader
          eyebrow="You stay in control"
          id="rp-privacy-title"
          title={
            <>
              Personal to you. <em>Private by choice.</em>
            </>
          }
          description="You can see, change, or remove what Rusiv remembers. Your information should help you, not box you in."
          variant="side"
        />

        <div className="rp-control-grid">
          {controlPrinciples.map((principle, index) => (
            <article data-rusiv-reveal key={principle.title}>
              <span aria-hidden="true">0{index + 1}</span>
              <div>
                <h3>{principle.title}</h3>
                <p>{principle.text}</p>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="rp-privacy-link-wrap" data-rusiv-reveal>
        <p>Your memory. Your choice.</p>
        <Link className="rp-text-link" to="/privacy">
          Read the Privacy Policy
          <span aria-hidden="true">↗</span>
        </Link>
      </div>
    </div>
  </section>
);

const RusivFinalCTA = () => (
  <section
    className="rp-final-cta"
    aria-labelledby="rp-final-title"
  >
    <div className="rp-final-orbit rp-final-orbit-one" aria-hidden="true" />
    <div className="rp-final-orbit rp-final-orbit-two" aria-hidden="true" />
    <div className="rp-container rp-final-content" data-rusiv-reveal>
      <p className="rp-eyebrow">An AI made for you</p>
      <h2 id="rp-final-title">
        <em>Ready when you are.</em>
      </h2>
      <p>
        Talk, plan, or just think out loud. Rusiv is ready.
      </p>
      <p className="rp-brand-line rp-brand-line-light" aria-label="you with you">
        you with <strong>YOU!</strong>
      </p>
      <div className="rp-actions rp-final-actions">
        <a
          className="rp-button rp-button-light"
          href="https://rusiv.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Try Rusiv
        </a>
        <button
          type="button"
          className="rp-button rp-button-outline-light"
          data-bs-toggle="modal"
          data-bs-target="#feedbackModal"
        >
          Share Feedback
        </button>
      </div>
    </div>
  </section>
);

const MESSAGE_THRESHOLDS = [0, 0.24, 0.48, 0.72];

const ChatMessages = ({ chapter, localProgress = 1, staticMode = false }) => (
  <ol className="rp-chat-messages" aria-label={`${chapter.label} conversation`}>
    {chapter.messages.map((message, index) => {
      const visible = staticMode || localProgress >= MESSAGE_THRESHOLDS[index];

      return (
        <li
          className={`rp-chat-message rp-chat-message-${message.role}${
            visible ? ' is-visible' : ''
          }`}
          aria-hidden={!visible}
          key={`${chapter.id}-${index}`}
        >
          {message.role === 'rusiv' && (
            <span className="rp-chat-avatar" aria-hidden="true">
              R
            </span>
          )}
          <div>
            <span className="rp-sr-only">
              {message.role === 'rusiv' ? 'Rusiv says: ' : 'You say: '}
            </span>
            {message.text}
          </div>
        </li>
      );
    })}
  </ol>
);

const StaticChatStory = () => (
  <section
    className="rp-section rp-chat-section rp-chat-section-static"
    aria-labelledby="rp-chat-title"
  >
    <div className="rp-container">
      <header className="rp-chat-static-header">
        <p className="rp-eyebrow">A conversation with Rusiv</p>
        <h2 id="rp-chat-title">
          See how Rusiv can <em>remember</em> and help.
        </h2>
        <p>
          Here are four simple moments where Rusiv uses memory, care, clear
          thinking, and useful information.
        </p>
      </header>
      <div className="rp-chat-static-grid">
        {chatChapters.map((chapter, index) => (
          <article className="rp-chat-static-card" key={chapter.id}>
            <p className="rp-chat-chapter-count">
              Chapter {index + 1} of {chatChapters.length}
            </p>
            <h3>{chapter.label}</h3>
            <ChatMessages chapter={chapter} staticMode />
          </article>
        ))}
      </div>
    </div>
  </section>
);

const ImmersiveChatStory = ({ reducedMotion }) => {
  const sectionRef = useRef(null);
  const frameRef = useRef(null);
  const [chatState, setChatState] = useState({
    activeChapter: 0,
    localProgress: 0,
    overallProgress: 0,
  });

  useEffect(() => {
    if (reducedMotion) return undefined;

    const updateProgress = () => {
      frameRef.current = null;
      if (!sectionRef.current) return;

      const { top, height } = sectionRef.current.getBoundingClientRect();
      const scrollableDistance = Math.max(1, height - window.innerHeight);
      const overallProgress = Math.max(
        0,
        Math.min(1, -top / scrollableDistance)
      );
      const chapterPosition = overallProgress * chatChapters.length;
      const activeChapter = Math.min(
        chatChapters.length - 1,
        Math.floor(chapterPosition)
      );
      const localProgress =
        overallProgress === 1 ? 1 : chapterPosition - activeChapter;

      setChatState((current) => {
        if (
          current.activeChapter === activeChapter &&
          Math.abs(current.localProgress - localProgress) < 0.008 &&
          Math.abs(current.overallProgress - overallProgress) < 0.008
        ) {
          return current;
        }

        return { activeChapter, localProgress, overallProgress };
      });
    };

    const requestUpdate = () => {
      if (frameRef.current === null) {
        frameRef.current = window.requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [reducedMotion]);

  if (reducedMotion) return <StaticChatStory />;

  const { activeChapter, localProgress, overallProgress } = chatState;
  const chapter = chatChapters[activeChapter];

  const goToChapter = (index) => {
    if (!sectionRef.current) return;

    const sectionTop =
      sectionRef.current.getBoundingClientRect().top + window.scrollY;
    const scrollableDistance =
      sectionRef.current.offsetHeight - window.innerHeight;
    const targetProgress = (index + 0.04) / chatChapters.length;

    window.scrollTo({
      top: sectionTop + scrollableDistance * targetProgress,
      behavior: 'smooth',
    });
  };

  return (
    <section
      ref={sectionRef}
      className={`rp-chat-section rp-chat-mood-${chapter.mood}`}
      aria-labelledby="rp-chat-title"
    >
      <div className="rp-chat-sticky">
        <div className="rp-chat-atmosphere" aria-hidden="true">
          <div className="rp-chat-atmosphere-orb" />
          {chapter.atmosphere.map((fragment, index) => (
            <span
              className={`rp-chat-fragment rp-chat-fragment-${index + 1}`}
              key={`${chapter.id}-${fragment}`}
            >
              {fragment}
            </span>
          ))}
        </div>

        <div className="rp-chat-shell">
          <header className="rp-chat-heading">
            <div>
              <p className="rp-chat-chapter-count">
                Chapter {activeChapter + 1} of {chatChapters.length}
              </p>
              <h2 id="rp-chat-title">
                See how Rusiv can <em>remember</em> and help.
              </h2>
            </div>
            <p className="rp-chat-active-label">{chapter.label}</p>
          </header>

          <div className="rp-chat-conversation">
            <ChatMessages
              chapter={chapter}
              localProgress={localProgress}
            />
          </div>

          <nav
            className="rp-chat-chapter-nav"
            aria-label="Conversation chapters"
          >
            {chatChapters.map((item, index) => (
              <button
                type="button"
                className={index === activeChapter ? 'is-active' : ''}
                aria-current={index === activeChapter ? 'step' : undefined}
                aria-label={`Show chapter ${index + 1}: ${item.label}`}
                onClick={() => goToChapter(index)}
                key={item.id}
              >
                <span>{index + 1}</span>
                <small>{item.label}</small>
              </button>
            ))}
          </nav>

          <div
            className="rp-chat-progress"
            role="progressbar"
            aria-label="Conversation story progress"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={Math.round(overallProgress * 100)}
            aria-valuetext={`${chapter.label}, chapter ${
              activeChapter + 1
            } of ${chatChapters.length}`}
          >
            <span
              aria-hidden="true"
              style={{ transform: `scaleX(${overallProgress})` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const ProductPage = () => {
  const pageRef = useRef(null);
  const reducedMotion = useReducedMotion();

  usePageMetadata(PRODUCT_METADATA);
  useProductScrollReveal(pageRef);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <main ref={pageRef} className="rp-page">
      <ScrollProgressBar />
      <RusivHero />
      <RusivDifference />
      <PersonalUnderstanding />
      <RusivCapabilities />
      <ImmersiveChatStory reducedMotion={reducedMotion} />
      <RusivInterface reducedMotion={reducedMotion} />
      <HowRusivHelps />
      <PrivacyAndControl />
      <RusivFinalCTA />
    </main>
  );
};

export default ProductPage;